import React, {useCallback, useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Heading, Text} from '@albert-io/atomic';
import {callTargetedAction, setUpStore} from 'client/framework';

import FreeResponseStore from '../FreeResponseQuestion.store';
import freeResponseActions from '../FreeResponseQuestion.actions';
import type {WSStore} from '../FreeResponseQuestion.types';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  answerInEnglishText,
  translatedInstructions
} from '../../shared/TranslationSection';
import useDraftGuess from '../../../../AssignmentPracticeView/DraftGuesses/useDraftGuess';

import WrittenSubmissionContext from './WrittenSubmission.context';
import WrittenSubmissionInput from './Editor/WrittenSubmissionInput.react';

interface Props {
  // May be undefined while the question's store has not yet been set up; the
  // draft-guess/store lookups below tolerate a missing store name.
  storeName?: string;
  submitted?: boolean;
  question: any;
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
}

const WrittenStudentSubmission = ({
  storeName,
  submitted = false,
  question,
  isViewingStudentWork = false
}: Props) => {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const {DraftGuessSaveStatus, startTimer} = useDraftGuess(storeName, submitted);

  const [store, setStore] = useState<null | WSStore>(null);

  const onChange = useCallback(
    (data) => {
      if (!submitted) {
        callTargetedAction({
          name: freeResponseActions.UPDATE_RESPONSE,
          payload: data,
          targetStore: storeName
        });
      }
    },
    [storeName, submitted]
  );

  useEffect(() => {
    // `setUpStore` returns the framework base `Store`; the FreeResponse store's
    // public surface is described by `WSStore`.
    const newStore = setUpStore(
      FreeResponseStore,
      storeName,
      question.getId(),
      question
    ) as unknown as WSStore;
    // oxlint-disable-next-line react/set-state-in-effect -- store swap plus delayed remount workaround for a Slate cursor out-of-bounds bug (see comment below)
    setStore(null);

    const timeout = setTimeout(() => {
      // hot swapping written submission input editor values causes a bug where if the previous cursor is out of bounds of the next
      // written submission, it causes an error. This only occurs when the next question is another written submission
      startTimer(); // start time_elapsed timer
      setStore(newStore);
    }, 100);

    return () => clearTimeout(timeout);
  }, [storeName, question, startTimer]);

  const editorState = store ? store.getValue() : null;
  const labelText = submitted
    ? isViewingStudentWork
      ? 'Student’s Response'
      : 'Your Response'
    : 'Provide Your Response Below';

  const Wrapper = showTranslations ? NoTranslationWarningHeader : React.Fragment;

  const wordCountText = showTranslations ? 'Conteo de palabras' : 'Word Count';

  return (
    <div className='mb-x4 w-full'>
      <Heading as='h2' size='s' id='wss-text-input'>
        {showTranslations ? translatedInstructions.freeResponse : labelText}
      </Heading>
      <Wrapper className='mb-x2' text={answerInEnglishText}>
        {store && editorState && (
          <WrittenSubmissionContext.Provider
            value={{store, storeName: storeName ?? null, submitted}}
          >
            <WrittenSubmissionInput
              onChange={onChange}
              value={editorState}
              readOnly={submitted}
              aria-labelledby='wss-text-input'
            />
            {!submitted ? (
              <span className='mt-x1 flex items-center'>
                <DraftGuessSaveStatus store={store} />
                <Text size='s' className='content-tertiary ml-auto'>
                  {wordCountText}: {store.getWordCount()}
                </Text>
              </span>
            ) : null}
          </WrittenSubmissionContext.Provider>
        )}
      </Wrapper>
    </div>
  );
};

export default WrittenStudentSubmission;
